import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { VerificationData } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { api } from '../services/api';
import { ShieldCheck, ShieldAlert, Award, Search, Calendar, FileCode, CheckCircle2 } from 'lucide-react';

interface VerifyBadgePageProps {
  initialBadgeId?: string;
}

export const VerifyBadgePage: React.FC<VerifyBadgePageProps> = ({ initialBadgeId }) => {
  const { badgeId: urlBadgeId } = useParams<{ badgeId: string }>();
  const { t } = useLanguage();

  const targetId = urlBadgeId || initialBadgeId || 'bdg_demo_123456';
  const [badgeIdInput, setBadgeIdInput] = useState(targetId);
  const [verification, setVerification] = useState<VerificationData | null>(null);
  const [loading, setLoading] = useState(false);
  const [showJsonLd, setShowJsonLd] = useState(false);

  const handleVerify = async (idToVerify: string) => {
    if (!idToVerify.trim()) return;
    setLoading(true);
    try {
      const data = await api.verifyBadge(idToVerify);
      setVerification(data);
    } catch (e: any) {
      alert(e.message || 'Error verifying credential');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (targetId) {
      setBadgeIdInput(targetId);
      handleVerify(targetId);
    }
  }, [targetId]);

  return (
    <div style={{ padding: '32px 24px', maxWidth: '840px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: 'var(--gradient-badge)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '16px',
          boxShadow: 'var(--shadow-glow)'
        }}>
          <Award size={32} color="#ffffff" />
        </div>
        <h1 style={{ fontSize: '30px', fontWeight: 800 }}>
          {t('Portal Público de Verificación de Insignias', 'Public Open Badges Verification Portal')}
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '6px' }}>
          {t(
            'Valide la autenticidad criptográfica SHA-256 y la vigencia anual (365 días) de certificados emitidos bajo el estándar Open Badges 3.0 (W3C).',
            'Validate SHA-256 cryptographic proof and 1-year vigency (365 days) for Open Badges 3.0 W3C Verifiable Credentials.'
          )}
        </p>
      </div>

      {/* Search Bar */}
      <div className="glass-card" style={{ padding: '16px', display: 'flex', gap: '12px', marginBottom: '32px' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <input
            type="text"
            className="input"
            value={badgeIdInput}
            onChange={(e) => setBadgeIdInput(e.target.value)}
            placeholder={t('Ingrese el ID de la insignia (ej. bdg_demo_123456)', 'Enter Badge ID (e.g. bdg_demo_123456)')}
            style={{ paddingLeft: '40px' }}
          />
          <Search size={18} style={{ position: 'absolute', left: '12px', top: '13px', color: 'var(--text-muted)' }} />
        </div>
        <button
          className="btn btn-primary"
          onClick={() => handleVerify(badgeIdInput)}
          disabled={loading}
        >
          {loading ? t('Verificando...', 'Verifying...') : t('Verificar', 'Verify')}
        </button>
      </div>

      {/* Verification Card Result */}
      {verification && (
        <div className="glass-card" style={{
          padding: '32px',
          border: verification.valid ? '1px solid var(--accent-success)' : '1px solid var(--accent-danger)',
          boxShadow: verification.valid ? '0 0 20px rgba(16, 185, 129, 0.15)' : '0 0 20px rgba(239, 68, 68, 0.15)',
        }}>
          {/* Status Badge */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {verification.valid ? (
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(16, 185, 129, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ShieldCheck size={26} color="var(--accent-success)" />
                </div>
              ) : (
                <div style={{ width: '44px', height: '44px', borderRadius: '50%', background: 'rgba(239, 68, 68, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ShieldAlert size={26} color="var(--accent-danger)" />
                </div>
              )}
              <div>
                <div style={{ fontSize: '18px', fontWeight: 800, color: verification.valid ? 'var(--accent-success)' : 'var(--accent-danger)' }}>
                  {verification.valid ? t('INSIGNIA VÁLIDA Y AUTÉNTICA', 'VALID & AUTHENTIC BADGE') : t('INSIGNIA EXPIRADA O NO VÁLIDA', 'INVALID OR EXPIRED BADGE')}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                  ID: <code style={{ color: 'var(--accent-primary)' }}>{verification.badgeId}</code>
                </div>
              </div>
            </div>

            <span style={{
              background: verification.valid ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
              color: verification.valid ? 'var(--accent-success)' : 'var(--accent-danger)',
              padding: '6px 14px',
              borderRadius: '20px',
              fontWeight: 700,
              fontSize: '12px',
            }}>
              {verification.status}
            </span>
          </div>

          {/* Message Banner */}
          {verification.message && (
            <div style={{
              padding: '12px 16px',
              borderRadius: 'var(--radius-sm)',
              marginBottom: '20px',
              background: verification.valid ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
              border: verification.valid ? '1px solid rgba(16, 185, 129, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
              color: verification.valid ? '#34d399' : '#f87171',
              fontSize: '13px',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {verification.valid ? <CheckCircle2 size={16} /> : <ShieldAlert size={16} />}
              {verification.message}
            </div>
          )}

          {/* Details Table */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: 'var(--radius-sm)' }}>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600 }}>
                {t('Curso Acreditado', 'Accredited Course')}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 700, marginTop: '4px' }}>
                {verification.courseTitle || 'SG-SST: Sistema de Gestión'}
              </div>
            </div>

            <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: 'var(--radius-sm)' }}>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600 }}>
                {t('Titular / Estudiante', 'Recipient / Learner')}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 700, marginTop: '4px' }}>
                {verification.recipientEmail || 'learner@empresa.com'}
              </div>
            </div>

            <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: 'var(--radius-sm)' }}>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600 }}>
                {t('Fecha de Emisión', 'Issued On')}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 700, marginTop: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Calendar size={14} /> {verification.issuedOn || '2026-08-04'}
              </div>
            </div>

            <div style={{ background: 'var(--bg-secondary)', padding: '16px', borderRadius: 'var(--radius-sm)' }}>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: 600 }}>
                {t('Vigencia Restante', 'Vigency Remaining')}
              </div>
              <div style={{
                fontSize: '14px',
                fontWeight: 700,
                marginTop: '4px',
                color: verification.valid ? 'var(--accent-success)' : 'var(--accent-danger)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                {verification.valid ? (
                  <>
                    <CheckCircle2 size={14} /> {verification.vigencyRemainingDays ?? 365} {t('días restantes (Anual)', 'days remaining (Annual)')}
                  </>
                ) : (
                  <>
                    <ShieldAlert size={14} /> {verification.vigencyRemainingDays ?? 0} {t('días restantes', 'days remaining')} ({verification.status})
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Cryptographic payload drawer */}
          <div>
            <button
              className="btn btn-secondary"
              style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '8px' }}
              onClick={() => setShowJsonLd(!showJsonLd)}
            >
              <FileCode size={16} />
              {showJsonLd ? t('Ocultar Payload W3C JSON-LD', 'Hide W3C JSON-LD Payload') : t('Ver Metadata Criptográfica JSON-LD (Open Badges 3.0)', 'View Open Badges 3.0 JSON-LD Metadata')}
            </button>

            {showJsonLd && (
              <pre style={{
                marginTop: '16px',
                padding: '16px',
                background: '#090d16',
                border: '1px solid var(--border-color)',
                borderRadius: 'var(--radius-sm)',
                color: '#34d399',
                fontSize: '11px',
                overflowX: 'auto',
                lineHeight: 1.5,
              }}>
                {JSON.stringify(
                  {
                    '@context': [
                      'https://www.w3.org/ns/credentials/v2',
                      'https://purl.imsglobal.org/spec/ob/v3p0/context.json',
                    ],
                    id: `urn:uuid:${verification.badgeId}`,
                    type: ['VerifiableCredential', 'OpenBadgeCredential'],
                    issuer: {
                      id: 'https://akretrix.com/issuer/org_akretrix_root',
                      type: 'Profile',
                      name: 'Akretrix Compliance Certification Authority',
                    },
                    issuanceDate: verification.issuedOn,
                    expirationDate: verification.validUntil,
                    credentialSubject: {
                      id: `did:key:${verification.recipientEmail}`,
                      type: 'AchievementSubject',
                      achievement: {
                        id: 'urn:achievement:sg-sst-2026',
                        type: 'Achievement',
                        name: verification.courseTitle,
                        description: 'Cumplimiento normativo certificado bajo estándares legales.',
                      },
                    },
                    proof: {
                      type: 'Ed25519Signature2020',
                      created: new Date().toISOString(),
                      verificationMethod: 'https://akretrix.com/keys/ed25519-public-key',
                      proofPurpose: 'assertionMethod',
                      proofValue: 'z3hG9v...SHA256_CRYPTOGRAPHIC_SEAL',
                    },
                  },
                  null,
                  2
                )}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
